import { API_BASE } from "../../config/api";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Topbar from "../../components/Topbar";

export default function AdminQuestions() {
  const { id } = useParams();
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState("");

  // ================= LOAD QUESTIONS =================
  useEffect(() => {
    if (!id) return;

    fetch(`${API_BASE}/admin/tests/${id}/questions`, {
      credentials: "include", // ✅ cookie
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load questions");
        return res.json();
      })
      .then((data) => {
        const safe = (Array.isArray(data) ? data : []).map((q) => ({
          _id: q._id,
          questionNo: q.questionNo,
          type: q.type || "MCQ",
          questionText: q.questionText || "",
          options:
            q.type === "CODING"
              ? {
                  sampleInput: q.options?.sampleInput || "",
                  sampleOutput: q.options?.sampleOutput || "",
                }
              : {
                  A: q.options?.A || "",
                  B: q.options?.B || "",
                  C: q.options?.C || "",
                  D: q.options?.D || "",
                },
          correctOption: q.correctOption || "",
        }));

        setQuestions(safe);
      })
      .catch((err) => setError(err.message));
  }, [id]);

  // ================= UPDATE HELPERS =================
  const updateQuestion = (qid, updater) => {
    setQuestions((prev) =>
      prev.map((q) => (q._id === qid ? updater(q) : q))
    );
  };

  const saveQuestion = (qid) => {
    const q = questions.find((x) => x._id === qid);
    if (!q) return;

    fetch(`${API_BASE}/admin/questions/${q._id}`, {
      method: "PUT",
      credentials: "include", // ✅ cookie
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        questionText: q.questionText,
        options: q.options,
        correctOption: q.correctOption,
      }),
    }).catch(console.error);
  };

  if (error) {
    return <div className="ml-64 p-6 text-red-400">{error}</div>;
  }

  if (!questions.length) {
    return (
      <div className="ml-64 p-6 text-white">
        Loading questions...
      </div>
    );
  }

  return (
    <div className="ml-64 min-h-screen bg-gray-900 text-white">
      <Topbar title="Admin • Questions" />

      <div className="p-6">
        <h1 className="text-xl font-bold mb-6">
          Add Questions (48 MCQ + 2 Coding)
        </h1>

        {questions.map((q) => (
          <div
            key={q._id}
            className="bg-gray-900 border border-gray-700 p-4 mb-4 rounded"
          >
            <p className="font-semibold mb-2">
              Question {q.questionNo} ({q.type})
            </p>

            <textarea
              className="w-full bg-gray-800 p-2 mb-3"
              value={q.questionText}
              placeholder="Question text"
              onChange={(e) =>
                updateQuestion(q._id, (old) => ({
                  ...old,
                  questionText: e.target.value,
                }))
              }
              onBlur={() => saveQuestion(q._id)}
            />

            {q.type === "MCQ" &&
              ["A", "B", "C", "D"].map((opt) => (
                <input
                  key={opt}
                  className="w-full bg-gray-800 p-2 mb-2"
                  placeholder={`Option ${opt}`}
                  value={q.options[opt]}
                  onChange={(e) =>
                    updateQuestion(q._id, (old) => ({
                      ...old,
                      options: {
                        ...old.options,
                        [opt]: e.target.value,
                      },
                    }))
                  }
                  onBlur={() => saveQuestion(q._id)}
                />
              ))}

            {q.type === "MCQ" && (
              <select
                className="bg-gray-800 p-2 mt-2"
                value={q.correctOption}
                onChange={(e) =>
                  updateQuestion(q._id, (old) => ({
                    ...old,
                    correctOption: e.target.value,
                  }))
                }
                onBlur={() => saveQuestion(q._id)}
              >
                <option value="">Correct Option</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
              </select>
            )}

            {q.type === "CODING" && (
              <>
                <textarea
                  className="w-full bg-gray-800 p-2 mb-2"
                  placeholder="Sample Input"
                  value={q.options.sampleInput}
                  onChange={(e) =>
                    updateQuestion(q._id, (old) => ({
                      ...old,
                      options: {
                        ...old.options,
                        sampleInput: e.target.value,
                      },
                    }))
                  }
                  onBlur={() => saveQuestion(q._id)}
                />

                <textarea
                  className="w-full bg-gray-800 p-2"
                  placeholder="Sample Output"
                  value={q.options.sampleOutput}
                  onChange={(e) =>
                    updateQuestion(q._id, (old) => ({
                      ...old,
                      options: {
                        ...old.options,
                        sampleOutput: e.target.value,
                      },
                    }))
                  }
                  onBlur={() => saveQuestion(q._id)}
                />
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
